import classnames from 'classnames';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import Theme from 'styles/theme';

export const BORDER_RADIUS = 30;

class PillToggle extends PureComponent {
  constructor(props) {
    super(props);

    this.navigationItemRefs = this.props.tabs.map(() => React.createRef());

    const startingDistance = 100 / this.props.tabs.length;
    const startingLocation = this.props.activeIndex * startingDistance;

    this.state = { coverWidth: `%${startingDistance}`, coverOffset: `%${startingLocation}` };
    this.coverWidthTimeout = null;
  }

  componentDidMount() {
    this.coverWidthTimeout = setTimeout(() => this.calculateCoverWidth(), 100);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.activeIndex !== this.props.activeIndex) {
      if (this.coverWidthTimeout) {
        clearTimeout(this.coverWidthTimeout);
      }
      this.coverWidthTimeout = setTimeout(() => this.calculateCoverWidth(), 100);
    }
  }

  componentWillUnmount() {
    if (this.coverWidthTimeout) {
      clearTimeout(this.coverWidthTimeout);
    }
  }

  calculateCoverWidth() {
    const { activeIndex } = this.props;
    const ref = this.navigationItemRefs[activeIndex];
    const coverWidth = ref?.current.getBoundingClientRect().width;
    const coverOffset = ref?.current.offsetLeft;

    this.setState({ coverWidth: `${coverWidth}px`, coverOffset: `${coverOffset}px` });
  }

  render() {
    const { color, onClick, tabs, activeIndex, ...otherProps } = this.props;
    const { coverWidth, coverOffset } = this.state;

    if (isEmpty(tabs)) {
      return null;
    }

    return (
      <div className="PillToggle" {...otherProps}>
        {tabs.map((navigationItem, index) => (
          <a
            className={classnames('PillToggle__button', {
              'PillToggle__button--active': activeIndex === index,
            })}
            ref={this.navigationItemRefs[index]}
            onClick={() => onClick(navigationItem)}
            href={navigationItem.href}
            key={navigationItem?.href}
          >
            <span className="PillToggle__text">{navigationItem.text}</span>
          </a>
        ))}

        <div className="PillToggle__cover" />

        <style jsx>
          {`
            .PillToggle {
              position: relative;
              display: inline-flex;
              border-radius: ${Theme.get('border-radius-capsule-px')};
              background-color: ${Theme.get('colors-white')};
              border: 1px solid ${Theme.get('colors-black-20')};
              margin: 5px 0; //this is to match the pill button style
              min-height: 32px; // this is the min height of icon buttons in the pill button style
              max-height: 32px;
            }

            .PillToggle__text {
              font-size: ${Theme.get('font-size-small-px')};
              font-weight: ${Theme.get('font-weight-semi-bold')};
              display: inline-block;
              color: ${Theme.get('colors-black')};
              text-transform: uppercase;
              letter-spacing: ${Theme.get('letter-spacing-extra-large-px')};
              margin: 0;
              color: ${color};
              transition: color ${Theme.get('transition-timing-ease-in-out')}
                ${Theme.get('transition-duration-ms')};
            }

            .PillToggle__button--active {
              pointer-events: none;
            }

            .PillToggle__button--active .PillToggle__text {
              color: ${Theme.get('colors-white')};
            }

            .PillToggle__button {
              display: flex;
              align-items: center;
              text-decoration: none;
              appearance: none;
              border: none;
              outline: none;
              margin: 0;
              cursor: pointer;
              padding: ${Theme.get('spacing') * 0.5}px ${Theme.get('spacing') * 3}px;
              background: none;
              z-index: 3;
              border-radius: ${Theme.get('border-radius-capsule-px')};
            }

            .PillToggle__cover {
              position: absolute;
              top: 0;
              bottom: 0;
              width: calc(${coverWidth});
              left: calc(${coverOffset});
              transition:
                left ${Theme.get('transition-timing-ease-in-out')}
                  ${Theme.get('transition-duration-ms')},
                background-color ${Theme.get('transition-timing-ease-in-out')}
                  ${Theme.get('transition-duration-ms')},
                width ${Theme.get('transition-timing-ease-in-out')}
                  ${Theme.get('transition-duration-ms')};
              background-color: ${color};
              border: 2px solid ${Theme.get('colors-white')};
              border-radius: ${Theme.get('border-radius-capsule-px')};
              z-index: 2;
            }
          `}
        </style>
      </div>
    );
  }
}

PillToggle.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      href: PropTypes.string,
    })
  ),
  color: PropTypes.string,
  onClick: PropTypes.func,
  activeIndex: PropTypes.number,
};

PillToggle.defaultProps = {
  onClick: () => {},
  tabs: [],
  activeIndex: 0,
  color: Theme.get('colors-black'),
};

export default PillToggle;
